import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(num: number, decimals: number = 1): string {
  if (!Number.isFinite(num)) return '0';
  return num.toFixed(decimals);
}

export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m ago` : `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Format timestamp for audit logs in EST 12-hour format
 * Short format: "Dec 8, 3:45 PM" (fits in table columns)
 * Full format: "Dec 8, 2025 3:45 PM EST"
 */
export function formatAuditTimestamp(timestamp: string, short = true): string {
  const date = new Date(timestamp);

  if (short) {
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
}

/**
 * Format user role for display
 * Examples: "group_admin" → "Group Admin", "superadmin" → "Superadmin"
 */
export function formatUserRole(role: string): string {
  if (!role || role === '-') return '-';

  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getStatusColor(status: 'online' | 'offline'): string {
  return status === 'online' ? 'text-success' : 'text-error';
}

export function getSystemStatusColor(enabled: boolean): string {
  return enabled ? 'text-success' : 'text-muted-foreground';
}

/**
 * Format location address to show only city, state/country
 * Examples:
 *   "Downtown Los Angeles, CA 90012" -> "Los Angeles, CA"
 *   "Santa Monica, CA 90401" -> "Santa Monica, CA"
 *   "Brooklyn, NY 11201" -> "Brooklyn, NY"
 */
/**
 * Filter out synthetic __unassigned_* group IDs
 * These are frontend-only pseudo-IDs representing "unassigned units" within companies
 * They are not valid UUIDs and cannot be sent to the backend
 */
export function filterValidGroupIds(groupIds: string[]): string[] {
  return groupIds.filter(id => !id.startsWith('__unassigned'));
}

export function formatLocationShort(address: string): string {
  // Remove ZIP code (5 digits, optionally followed by -XXXX)
  const formatted = address.replace(/\s+\d{5}(-\d{4})?$/, '');
  
  // Split by comma to separate city from state
  const parts = formatted.split(',').map(p => p.trim());
  
  if (parts.length >= 2) {
    const firstPart = parts[0];
    const words = firstPart.split(' ');
    
    // If first part has multiple words, it's likely "Neighborhood City Name"
    // Take the last 2 words as the city (handles "Los Angeles", "San Francisco", etc.)
    if (words.length > 2) {
      const city = words.slice(-2).join(' ');
      return `${city}, ${parts[1]}`;
    }
  }
  
  // Default: just return formatted address (ZIP removed)
  return formatted;
}
